import { useState } from 'react';
import { Check, MoreHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Meter, ScoreArc } from './resumeCheckUi';

/**
 * The product, shown as the product.
 *
 * What stood here was a looping abstraction — bars settling from grey to green under
 * a sweeping line, with pastel labels floating around it. It animated constantly,
 * said nothing concrete, and belonged to no other page we own.
 *
 * The marketing site makes this argument a different way and it is the better one: a
 * realistic frame around a real screen, with one thing in it you can click. There is
 * no looping animation anywhere on that site — a single rise on entry, colour
 * transitions on hover, and demos that hold a state. This follows that.
 */

const STAGES = ['Upload', 'Parse', 'Match', 'Score'] as const;

const MISSING = ['Kubernetes', 'AWS', 'Kafka'];
const FOUND = ['Node.js', 'TypeScript', 'Postgres'];

function Tokens({
  label,
  terms,
  tone,
}: {
  label: string;
  terms: string[];
  tone: 'danger' | 'positive';
}) {
  return (
    <div className="space-y-2.5">
      <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-ink-subtle">{label}</p>
      <ul className="flex flex-wrap gap-1.5">
        {terms.map((term) => (
          <li
            key={term}
            className={cn(
              'rounded-full border px-2.5 py-1 text-[12.5px] font-medium text-ink',
              tone === 'danger'
                ? 'border-danger/30 bg-danger-soft/50'
                : 'border-positive/30 bg-positive-soft/50'
            )}
          >
            {term}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ReportMockup() {
  // Starts on the last stage: the point is the finished report, and the stepper is
  // there to say what produced it, not to make somebody work for it.
  const [stage, setStage] = useState(STAGES.length - 1);

  return (
    <div className="relative motion-safe:animate-rise">
      <div
        aria-hidden
        className="pointer-events-none absolute -inset-8 -z-10 rounded-full bg-brand/10 blur-3xl"
      />

      <div className="overflow-hidden rounded-2xl border border-ink/10 bg-surface shadow-[0_30px_100px_-35px_color-mix(in_oklab,var(--color-brand)_45%,transparent)]">
        {/* The frame. Says "this is a screen" in one line, without a screenshot that
            would go stale the next time the report changes. */}
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="flex gap-1.5">
              <span className="size-2 rounded-full bg-danger/60" />
              <span className="size-2 rounded-full bg-warning/70" />
              <span className="size-2 rounded-full bg-brand/40" />
            </div>
            <span className="hidden text-xs text-ink-subtle sm:inline">
              hireorbit.com / resume-check
            </span>
          </div>
          <div className="flex items-center gap-2 text-xs text-ink-subtle">
            <span className="size-2 animate-pulse rounded-full bg-brand" />
            Example
            <MoreHorizontal className="size-4" />
          </div>
        </div>

        <div className="p-5 sm:p-6">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-ink-subtle">
              Senior Backend Engineer
            </p>
            <span className="text-xs text-brand">10 seconds</span>
          </div>

          {/* The four things that happen to a CV, as a stepper you can move. */}
          <div className="mt-4 flex items-center gap-0">
            {STAGES.map((name, index) => (
              <button
                key={name}
                type="button"
                onClick={() => setStage(index)}
                className="group flex min-w-0 flex-1 flex-col gap-2 text-left"
                aria-pressed={index === stage}
              >
                <div className="flex w-full items-center gap-2">
                  <span
                    className={cn(
                      'grid size-7 shrink-0 place-items-center rounded-full border text-[11px] font-bold transition-colors',
                      index <= stage
                        ? 'border-brand bg-brand text-white dark:text-ink-inverted'
                        : 'border-line bg-surface text-ink-subtle'
                    )}
                  >
                    {index < stage ? <Check className="size-3.5" /> : String(index + 1).padStart(2, '0')}
                  </span>
                  {index < STAGES.length - 1 && (
                    <span className={cn('h-px flex-1', index < stage ? 'bg-brand' : 'bg-line')} />
                  )}
                </div>
                <span
                  className={cn(
                    'text-[11.5px] transition-colors',
                    index === stage ? 'font-semibold text-ink' : 'text-ink-subtle'
                  )}
                >
                  {name}
                </span>
              </button>
            ))}
          </div>

          <div className="mt-6 grid gap-5 border-t border-line pt-5 sm:grid-cols-[auto_minmax(0,1fr)] sm:items-center">
            <div className="flex justify-center">
              <ScoreArc value={72} size={132} label="Possible match" />
            </div>
            <div className="space-y-3">
              <Meter label="Skills" value={80} />
              <Meter label="Keyword coverage" value={65} />
              <Meter label="Format" value={90} />
            </div>
          </div>

          {/* Two labelled groups. Both sets of pills sat under "Asked for, not
              found", so the four the CV does have were captioned as missing — the
              one thing this panel exists to tell them apart. */}
          <div className="mt-5 grid gap-4 border-t border-line pt-5 sm:grid-cols-2">
            <Tokens label="Asked for, not found" terms={MISSING} tone="danger" />
            <Tokens label="Already coming through" terms={FOUND} tone="positive" />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * A numbered marker for a step of the form, filled once that step is done.
 *
 * The same circle the hero mockup's stepper draws, because the form is literally the
 * first two stages of it — "1 ·" and "2 ·" set as plain text said the same thing in a
 * vocabulary the rest of the page does not use, and gave no feedback that a half was
 * finished.
 */
export function StepLabel({
  index,
  done,
  children,
}: {
  index: number;
  done: boolean;
  children: string;
}) {
  return (
    <span className="flex items-center gap-2.5">
      <span
        className={cn(
          'grid size-6 shrink-0 place-items-center rounded-full border text-[11px] font-bold transition-colors',
          done
            ? 'border-brand bg-brand text-white dark:text-ink-inverted'
            : 'border-line bg-surface text-ink-subtle'
        )}
      >
        {done ? <Check className="size-3.5" aria-hidden /> : index}
      </span>
      <span className="text-[14px] font-semibold text-ink">{children}</span>
    </span>
  );
}
